import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const KEY = 'relay-cookie-consent';

export const getAnalyticsConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY))?.choice === 'accepted';
  } catch {
    return false;
  }
};

// Analytics wiring point: when analytics are added, load their script ONLY if
// getAnalyticsConsent() is true, and subscribe to the 'relay:consent' window event
// (detail: { choice: 'accepted' | 'essential' }) to load them after in-page consent.

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem(KEY));
    } catch {}
    if (!stored) {
      const t = setTimeout(() => setVisible(true), 1400);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const open = () => setVisible(true);
    window.addEventListener('relay:open-cookie-preferences', open);
    return () => window.removeEventListener('relay:open-cookie-preferences', open);
  }, []);

  const choose = (choice) => {
    try {
      localStorage.setItem(KEY, JSON.stringify({ choice, at: new Date().toISOString() }));
    } catch {}
    window.dispatchEvent(new CustomEvent('relay:consent', { detail: { choice } }));
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          role="dialog"
          aria-label="Cookie preferences"
          data-testid="cookie-banner"
          className="fixed bottom-4 left-4 right-4 z-[60] rounded-xl border border-[#E5E7EB] bg-white p-5 shadow-[0_24px_48px_-16px_rgba(11,31,26,0.25)] sm:right-auto sm:max-w-sm"
        >
          <p className="font-op text-[10px] font-semibold uppercase tracking-[0.22em] text-[#0F5C4A]">
            Cookies on relaybyavenir.com
          </p>
          <p className="mt-2.5 text-sm leading-relaxed text-[#4B5563]">
            We use essential storage to run this site. We&rsquo;d also like to use analytics cookies to understand
            how the site is used — only with your permission.
          </p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <button data-testid="cookie-accept" onClick={() => choose('accepted')} className="btn-primary !px-4 !py-2.5 !text-[13px]">
              Accept analytics
            </button>
            <button data-testid="cookie-essential" onClick={() => choose('essential')} className="btn-secondary !px-4 !py-2.5 !text-[13px]">
              Essential only
            </button>
          </div>
          <p className="mt-3 text-xs text-[#9CA3AF]">
            Read our{' '}
            <a href="/privacy" className="underline underline-offset-2 hover:text-[#0F5C4A]" data-testid="cookie-privacy-link">
              Privacy Policy
            </a>
            .
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
